import type {
	CollectionResponseData,
	MindItem,
	Product,
	SubscriptionResponse,
} from '@kimuno/api-sdk';

import { userType } from '../../constants';
import type { User } from '../../types/User';
import { flattenUserSubjects, getSeconds } from '../../utils';
import { getAttributionFromURLOrCookie } from '../../utils/attributionAmplitude';

const mapDocumentData = (document: MindItem) => {
	if (!document) {
		return;
	}

	return {
		doc_id: document.id,
	};
};

const mapProductData = (product: Product) => {
	if (!product) {
		return;
	}

	return {
		product_id: product.id,
		product_publishinghouse: product.publisher,
		product_classyears: product.classYears,
		product_schooltype: product.schoolTypes,
		product_subjects: product.subjects,
		product_attachments: product.hasAttachments,
	};
};

// Sources the utm_* keys from the shared last-touch attribution (URL params,
// falling back to the attribution cookie) instead of the historical hardcoded
// empty strings (SM-312). Works without a user — the checkout success page's
// purchase push fires before a session exists; only `account_created_ts` stays
// user-dependent. `utm_id` is additive next to the legacy five; `form_code`
// stays '' (no source for it in this app).
const generateUtmData = (user?: User) => {
	const { lastTouch } = getAttributionFromURLOrCookie();

	return {
		...(user?.createdAt != null
			? { account_created_ts: new Date(user.createdAt).getTime().toString() }
			: {}),
		form_code: '',
		utm_campaign: lastTouch.utm_campaign ?? '',
		utm_content: lastTouch.utm_content ?? '',
		utm_id: lastTouch.utm_id ?? '',
		utm_medium: lastTouch.utm_medium ?? '',
		utm_source: lastTouch.utm_source ?? '',
		utm_term: lastTouch.utm_term ?? '',
	};
};

const mapUserData = (user?: User) => {
	if (!user) {
		return;
	}

	const flatUserSubjects = user.subjectsFlattened;
	let user_type: number = userType.basis;
	if (user.isBibliothekUser) {
		user_type = userType.document;
	} else if (user.isKombiUser) {
		user_type = userType.kombi;
	} else if (user.isCourseUser) {
		user_type = userType.courses;
	}

	return {
		user_id: user.id || '',
		user_subject1: (flatUserSubjects[0] && flatUserSubjects[0].text) || '',
		user_subject2: (flatUserSubjects[1] && flatUserSubjects[1].text) || '',
		user_subject3: (flatUserSubjects[2] && flatUserSubjects[2].text) || '',
		user_type,
		user_is_in_trial: user.isInTrial,
	};
};

const mapCollectionData = (collection: CollectionResponseData) => {
	if (!collection) {
		return;
	}

	return {
		collection_id: collection._id,
		collection_public: collection.isPublic,
		collection_schooltype: collection.schoolType,
		collection_subject: collection.subjects.join(';'),
	};
};

const mapOther = (data: any) => {
	return data;
};

// Billing writes product_name, sub_status, sub_start_at, sub_end_at and
// had_trial on every lifecycle change for a user with Stripe history, so the
// web must not overwrite them with its (legacy Zuora) copy — unless that Zuora
// subscription is Active, which is newer than anything billing wrote (SUB-496).
// See "Attribute ownership" in packages/intercom/README.md.
const billingOwnsSubscriptionAttributes = (user: User, subscription?: SubscriptionResponse) =>
	(user.isStripeUser || user.hasStripeHistory) && subscription?.status !== 'Active';

const mapIntercomUserData = (user?: User, subscription?: SubscriptionResponse) => {
	if (!user) {
		return;
	}

	const userSubjects = user.subjects;
	const flatUserSubjects = flattenUserSubjects(userSubjects).map((s) => s.id);

	const mapped = {
		email: user.email,
		user_id: user.id,
		name: [user.firstName, user.lastName].filter((field) => !!field).join(' '),
		created_at: getSeconds(user.createdAt),
		gender: user.gender || '',
		account_type: user.isAdmin ? 'admin' : user.isPremium ? 'premium' : 'basic',
		country:
			(user.address && user.address.country) ||
			(user.invoiceAddress && user.invoiceAddress.country) ||
			'',
		school_type: user.schoolType,
		subjects: flatUserSubjects.join(', '),
		is_trainee: user.isTrainee || false,
		is_parent: user.isParent || false,
		is_stripe_user: user.isStripeUser,
		downloads: user.downloads.counter,
		even: Date.parse(user.createdAt.toString()) % 2 === 0,
		state: user.address && user.address.state,
		last_invoiced_at: null,
		account_balance: null,
		bank_account_type: null,
		...(billingOwnsSubscriptionAttributes(user, subscription)
			? {}
			: {
					product_name: subscription?.products[0].name,
					sub_status: subscription?.status,
					sub_start_at: subscription?.startDate,
					sub_end_at: subscription?.endDate,
					had_trial: subscription?.licence.hasTrial,
				}),
		rate_plan_name: subscription?.products[0].plan.name,
		licence_model_id: subscription?.licence.licenceModelId,
		seo_doc_id: user.redirectTo,
		teacher_roles: (user.teacherRoles ?? []).join(', '),
		school_name: user?.school?.name,
		school_state: user?.federalState,
		school_stage: user?.schoolStage,
		school_stages: (user.schoolStages ?? []).join(', '),
		group_id: user?.groupId,
	};

	return mapped;
};

const requiredPropertyMappers = {
	collection: mapCollectionData,
	document: mapDocumentData,
	other: mapOther,
	product: mapProductData,
	user: mapUserData,
	utm: generateUtmData,
	intercom: mapIntercomUserData,
};

export default requiredPropertyMappers;
